<template>
  <div :style="styleVars" class="svoi-base-swiper">
    <slot name="before" v-bind="slotBindings" />
    <div ref="nodeRef" :class="['items-container', { '-center': !isSwipeable }]">
      <div v-for="item of virtualizedItems" :key="item.id" class="item">
        <slot :item="item.source" name="item" />
      </div>
    </div>
    <slot name="after" v-bind="slotBindings" />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, onUnmounted, PropType, reactive, ref, watch } from 'vue-demi'
import type { SwiperBreakpoint, SwiperItemProp, SwiperResponsiveProp } from '@svoi-ui/interfaces/swiper'
import { uuid } from '@svoi-ui/shared/utils/helpers'
import { isNumber } from '@svoi-ui/shared/utils/guards'
import { debounce } from '@svoi-ui/shared/utils/decorators'

export default defineComponent({
  name: 'SvoiSwiperBase',
  props: {
    items: {
      type: Array as PropType<Array<SwiperItemProp>>,
      required: true
    },
    countVisible: {
      type: Number,
      default: 1
    },
    responsive: {
      type: Array as PropType<SwiperResponsiveProp>,
      default: () => []
    },
    speedSwipe: {
      type: Number,
      default: 4
    },
    infinite: {
      type: Boolean,
      default: false
    },
    autoplay: {
      type: Number,
      default: 0
    }
  },
  setup(props) {
    const defaultBreakpoint: SwiperBreakpoint = {
      gap: 20,
      minWidth: 0,
      maxWidth: undefined,
      countVisible: props.countVisible
    }

    const nodeRef = ref<HTMLDivElement | null>(null)

    const currentDeviceWidth = ref(0)

    /*
      0.2 - 20% от ширины карточки. Расстояние на которое можно выйти за пределы слайдера в начале и конце.
      Не работает в Infinite моде.
    */

    const coefficientOutOffset = 0.2

    /*
      Минимальное расстояние для начала свайпа (разница в PX которую прошла мышь с момента нажатия на клавишу и её отжатия)
      если расстояние меньше указанного, то эффекта свайпа не будет осуществляться
    */
    const dragThreshold = 5

    /*
      Текущее состояние смещения. Переменная показывает на сколько смещён текущий список
      Например, при 7 элементах и значении countVisible 3 (количество видимых элементов на экране), а так же swipeOffset = 0,
      будем означать что в видимой области будут элемента с 1 по 3 включительно массива элементов virtualizedItems.
      При значении swipeOffset = 2, будут видны элементы 2, 3, 4.
      Плавность обеспечивается за счет requestAnimationFrame учитывая normalizedSpeedSwipe скорость свайпа.
    */
    const swipeOffset = ref<number>(0)

    const options = reactive<{
      touchStartX: number
      movementX: number | null
      isSwiping: boolean
    }>({
      touchStartX: 0,
      movementX: 0,
      isSwiping: false
    })

    const normalizedSpeedSwipe = computed(() => props.speedSwipe * (1 / 120))

    const possibleOffset = computed(() =>
      Array.isArray(props.items) ? props.items.length - currentBreakpointVars.value.countVisible : 0
    )

    const requestFrame = (fps: number = 120) => {
      const now = performanceNow()

      const interval = 1000 / fps
      let then: number = now()
      let isCancel: boolean = false

      function performanceNow() {
        const time = Date.now()

        return () => Date.now() - time
      }

      function animation(frameCallback: () => void, time: number = now()) {
        if (isCancel) {
          return
        }

        requestAnimationFrame(now => animation(frameCallback, now))

        const delta = time - then

        if (delta > interval) {
          then = time - (delta % interval)

          frameCallback()
        }
      }

      const cancelAnimation = () => {
        isCancel = true
      }

      const runAnimation = (frameCallback: () => void) => {
        isCancel = false

        animation(frameCallback)
      }

      return { cancelAnimation, runAnimation }
    }

    const { cancelAnimation, runAnimation } = requestFrame()

    const sortedItems = ref(
      Array.isArray(props.items)
        ? props.items
            .map((item, index) => ({
              id: item.id || uuid(),
              order: item.order || index,
              source: item
            }))
            .sort((a, b) => a.order - b.order)
        : []
    )

    const validateResponsiveVars = (breakpoint: SwiperBreakpoint): SwiperBreakpoint => {
      const gap: number = breakpoint.gap ?? defaultBreakpoint.gap
      const countVisible: number = breakpoint.countVisible ?? defaultBreakpoint.countVisible

      return {
        minWidth: breakpoint.minWidth ?? defaultBreakpoint.minWidth,
        maxWidth: breakpoint.maxWidth ?? defaultBreakpoint.maxWidth,
        countVisible: validateCountVisible(countVisible),
        gap
      }
    }

    const validateCountVisible = (count: number) => {
      if (count < 0) {
        return 1
      }

      return count
    }

    const currentBreakpointVars = computed(() => {
      let r: SwiperBreakpoint = { ...defaultBreakpoint }

      for (const breakpoint of props.responsive) {
        const minWidth = breakpoint.minWidth || 0
        const maxWidth = breakpoint.maxWidth || null

        if (
          currentDeviceWidth.value >= minWidth &&
          (maxWidth === null || currentDeviceWidth.value <= maxWidth) &&
          currentDeviceWidth.value > 0
        ) {
          r = { ...breakpoint }
        }
      }

      return validateResponsiveVars(r)
    })

    const virtualizedItems = ref([...sortedItems.value])

    watch(
      () => currentBreakpointVars.value.countVisible,
      () => {
        swipeSteps(0)
      }
    )

    const styleVars = computed(() => ({
      '--swiper-gap': `${currentBreakpointVars.value.gap}px`,
      '--swiper-count-visible': currentBreakpointVars.value.countVisible,
      '--swiper-offset': swipeOffset.value
    }))

    const isSwipeable = computed(() => sortedItems.value.length >= currentBreakpointVars.value.countVisible)

    const isInfinite = computed(() => {
      if (!props.infinite && !props.autoplay) {
        return false
      }

      if (Math.ceil(currentBreakpointVars.value.countVisible) + 2 > sortedItems.value.length) {
        /* eslint-disable no-console */
        console.error(
          'SvoiSwiper: the number of visible elements must be at least 2 less than the total number of elements'
        )
        /* eslint-enable no-console */

        return false
      }

      return props.infinite || props.autoplay
    })

    const linearSwipeOffset = (currentOffset: number, targetOffset: number): number => {
      const direction = targetOffset > currentOffset ? 1 : -1
      const updatedOffset = currentOffset + normalizedSpeedSwipe.value * direction

      if (updatedOffset <= 0) {
        cancelAnimation()

        return 0
      }

      if (updatedOffset >= possibleOffset.value) {
        cancelAnimation()

        return possibleOffset.value
      }

      if ((direction > 0 && updatedOffset > targetOffset) || (direction < 0 && updatedOffset < targetOffset)) {
        cancelAnimation()

        return targetOffset
      }

      return updatedOffset
    }

    const infiniteSwipe = (currentOffset: number, targetOffset: number) => {
      const direction = targetOffset > currentOffset ? 1 : -1

      let scrolledElements = Math.abs(currentOffset - targetOffset)

      const infiniteSwipeOffset = (currentOffset: number): number => {
        const updatedOffset = currentOffset + normalizedSpeedSwipe.value * direction

        if (scrolledElements <= 0) {
          cancelAnimation()

          return Math.round(currentOffset)
        }

        if (Math.floor(currentOffset) !== Math.floor(updatedOffset) && !Number.isInteger(currentOffset)) {
          scrolledElements -= 1

          if (scrolledElements <= 0) {
            cancelAnimation()

            return direction > 0 ? Math.floor(updatedOffset) : Math.ceil(updatedOffset)
          }
        }

        return updateVirtualizedList(updatedOffset)
      }
      return { infiniteSwipeOffset }
    }

    const rightSwapVirtualizedList = (currentOffset: number) => {
      const swapVirtualizedItem = virtualizedItems.value.shift()

      if (!swapVirtualizedItem) return currentOffset

      virtualizedItems.value.push(swapVirtualizedItem)

      return currentOffset - 1
    }

    const leftSwapVirtualizedList = (currentOffset: number) => {
      const swapVirtualizedItem = virtualizedItems.value.pop()

      if (!swapVirtualizedItem) return currentOffset

      virtualizedItems.value.unshift(swapVirtualizedItem)

      return currentOffset + 1
    }

    const swipe = (targetOffset: number) => {
      if (!isNumber(targetOffset) || !Number.isInteger(targetOffset)) {
        /* eslint-disable no-console */
        console.error('SvoiSwiper: only integers are used for the swap function')
        /* eslint-enable no-console */

        return
      }

      const { infiniteSwipeOffset } = infiniteSwipe(swipeOffset.value, targetOffset)

      runAnimation(() => {
        if (isInfinite.value) {
          swipeOffset.value = infiniteSwipeOffset(swipeOffset.value)
        } else {
          swipeOffset.value = linearSwipeOffset(swipeOffset.value, targetOffset)
        }
      })
    }

    const swipeSteps = (count: number) => {
      const updatedSize = swipeOffset.value + count

      swipe(updatedSize)
    }

    const bindedSwipe = (targetOffset: number) => {
      updateAutoplay()

      swipe(targetOffset)
    }

    const bindedSteps = (count: number) => {
      updateAutoplay()

      swipeSteps(count)
    }

    // Common listeners
    const moveListener = (coefficient: number, newMovement: number) => {
      const updatedOffset = swipeOffset.value + coefficient

      if (updatedOffset >= possibleOffset.value + coefficientOutOffset || updatedOffset <= -coefficientOutOffset) {
        return
      }

      swipeOffset.value = updateVirtualizedList(updatedOffset)

      options.movementX = newMovement
    }

    const endListener = (isFloorRound: boolean) => {
      options.movementX = null

      if (nodeRef.value) {
        const roundingFunc = isFloorRound ? Math.floor : Math.ceil

        const animationOffset = roundingFunc(swipeOffset.value)

        runAnimation(() => {
          const updatedOffset =
            swipeOffset.value + normalizedSpeedSwipe.value * (swipeOffset.value - animationOffset > 0 ? -1 : 1)

          if (swipeOffset.value === 0 || swipeOffset.value === possibleOffset.value) {
            cancelAnimation()
          }

          if (updatedOffset < 0) {
            swipeOffset.value = Math.min(0, swipeOffset.value + normalizedSpeedSwipe.value)

            return
          }

          if (updatedOffset > possibleOffset.value) {
            swipeOffset.value = Math.max(possibleOffset.value, swipeOffset.value - normalizedSpeedSwipe.value)

            return
          }

          if (Math.abs(updatedOffset - animationOffset) <= normalizedSpeedSwipe.value) {
            swipeOffset.value = animationOffset

            cancelAnimation()
          } else {
            swipeOffset.value = updatedOffset
          }
        })
      }
    }

    // Desktop listeners
    const mouseDownListener = (event: MouseEvent) => {
      event.preventDefault()

      if (!isSwipeable.value) {
        return
      }

      options.movementX = 0
      options.touchStartX = event.clientX
      options.isSwiping = true

      cancelAnimation()

      clearAutoplay()
    }

    const mouseMoveListener = (event: MouseEvent) => {
      event.preventDefault()

      const diffX = event.clientX - options.touchStartX

      if (
        nodeRef.value &&
        typeof options.movementX === 'number' &&
        options.isSwiping &&
        Math.abs(diffX) > dragThreshold
      ) {
        const coefficientOffset =
          (currentBreakpointVars.value.countVisible / nodeRef.value.offsetWidth) *
          Math.max(nodeRef.value.offsetWidth / 1000, 1)

        const coefficient = coefficientOffset * (event.movementX > 0 ? -1 : 1)

        moveListener(coefficient, event.movementX)
      }
    }

    const mouseUpListener = (event: MouseEvent) => {
      if (!options.isSwiping) {
        return
      }

      event.preventDefault()

      const lastMovement = options.movementX
      const diffX = event.clientX - options.touchStartX

      if (
        typeof lastMovement === 'number' &&
        (Math.abs(diffX) > dragThreshold || !Number.isInteger(swipeOffset.value))
      ) {
        endListener(lastMovement > 0)
      }

      options.isSwiping = false
      options.touchStartX = 0

      startAutoplay()
    }

    // Mobile listeners
    const touchStart = (event: TouchEvent) => {
      const clientX = event.changedTouches[0].clientX

      if (!isSwipeable.value) {
        return
      }

      options.touchStartX = clientX
      options.movementX = clientX

      cancelAnimation()

      clearAutoplay()
    }

    const touchMove = (event: TouchEvent) => {
      const currentX = event.changedTouches[0].clientX

      if (typeof options.movementX === 'number') {
        const coefficient = -(currentX - options.movementX) * 0.001

        moveListener(coefficient, currentX)
      }
    }

    const touchEnd = (event: TouchEvent) => {
      const clientX = event.changedTouches[0].clientX

      endListener(clientX - options.touchStartX > 0)

      options.touchStartX = 0

      startAutoplay()
    }

    const updateVirtualizedList = (currentSwipe: number): number => {
      if (!isInfinite.value) {
        return currentSwipe
      }

      const normalizedRightOffset = Math.ceil(currentSwipe) + currentBreakpointVars.value.countVisible

      if (normalizedRightOffset - virtualizedItems.value.length >= 0) {
        return rightSwapVirtualizedList(currentSwipe)
      }

      const normalizedLeftOffset = Math.floor(currentSwipe)

      if (normalizedLeftOffset <= 0) {
        return leftSwapVirtualizedList(currentSwipe)
      }

      return currentSwipe
    }

    const handleResize = debounce(() => {
      currentDeviceWidth.value = window.innerWidth
    }, 100)

    const autoplay = () => {
      let timer: ReturnType<typeof setInterval> | null = null

      const startAutoplay = () => {
        if (props.autoplay > 0) {
          timer = setInterval(() => swipeSteps(1), props.autoplay)
        }
      }

      const updateAutoplay = () => {
        if (props.autoplay > 0) {
          clearAutoplay()

          timer = setInterval(() => swipeSteps(1), props.autoplay)
        }
      }

      const clearAutoplay = () => {
        if (timer) {
          clearInterval(timer)

          timer = null
        }
      }

      return { startAutoplay, updateAutoplay, clearAutoplay }
    }

    const { startAutoplay, updateAutoplay, clearAutoplay } = autoplay()

    onMounted(() => {
      startAutoplay()

      if (window) {
        currentDeviceWidth.value = window.innerWidth

        window.addEventListener('resize', handleResize)
      }

      swipeOffset.value = updateVirtualizedList(swipeOffset.value)

      if (nodeRef.value) {
        // Desktop listeners
        nodeRef.value.addEventListener('mousedown', mouseDownListener)

        nodeRef.value.addEventListener('mousemove', mouseMoveListener)

        document.addEventListener('mouseup', mouseUpListener)

        // Mobile listeners
        nodeRef.value.addEventListener('touchstart', touchStart)

        nodeRef.value.addEventListener('touchmove', touchMove)

        nodeRef.value.addEventListener('touchend', touchEnd)
      }
    })

    onUnmounted(() => {
      clearAutoplay()

      if (window) {
        window.removeEventListener('resize', handleResize)
      }

      // Desktop listeners
      document.removeEventListener('mouseup', mouseUpListener)

      if (nodeRef.value) {
        nodeRef.value.removeEventListener('mousedown', mouseDownListener)

        nodeRef.value.removeEventListener('mousedown', mouseMoveListener)

        // Mobile listeners
        nodeRef.value.removeEventListener('touchstart', touchStart)

        nodeRef.value.removeEventListener('touchmove', touchMove)

        nodeRef.value.removeEventListener('touchend', touchEnd)
      }
    })

    const slotBindings = computed(() => ({
      swipe: bindedSwipe,
      swipeSteps: bindedSteps,
      swipeOffset: swipeOffset.value,
      items: sortedItems.value,
      virtualizedItems: virtualizedItems.value,
      currentBreakpoint: currentBreakpointVars.value,
      isInfinite: isInfinite.value,
      sortedItems: sortedItems.value
    }))

    return {
      sortedItems,
      styleVars,
      slotBindings,
      swipe,
      nodeRef,
      virtualizedItems,
      currentDeviceWidth,
      isSwipeable
    }
  }
})
</script>

<style lang="scss">
@use '@svoi-ui/assets/scss/components/swiper.scss';
</style>
